import {
  LEGACY_ASSOCIATION_TYPES_GDA,
  LEGACY_ASSOCIATION_TYPES_VDA,
  RELATION_EXTRACTION_ASSOCIATION_TYPES,
} from '@/components/About/constants';
import { formatAssociationType } from './formatAssociationType';

/** Extra labels kept for display/search aliases; deduped against API lists. */
const EXTENDED_ASSOCIATION_TYPES_GDA = [
  'Drug Response',
  'Genetic Variation',
  'Germline Modifying Mutation',
] as const;

const EXTENDED_ASSOCIATION_TYPES_VDA = [
  'Drug Response',
  'Genetic Variation',
  'Protective',
] as const;

export function normalizeAssociationTypeToken(value: string): string {
  return value.toLowerCase().replace(/[\s_\-]+/g, '');
}

export function isDrugResponseAssociationType(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const token = normalizeAssociationTypeToken(value);
  return token === 'drugresponse' || token === 'drugresponsevariant';
}

export const DRUG_RESPONSE_EXPANDED_VALUES = [
  'Drug Response',
  'Drug Response Variant',
  'DrugResponse',
] as const;

const ASSOCIATION_TYPE_BACKEND_VALUES: Record<string, string> = {
  causalorcontributing: 'Causal Or Contributing',
  chromosomalrearrangement: 'Chromosomal Rearrangement',
  drugresponse: 'Drug Response',
  geneticvariation: 'Genetic Variation',
  germlinecausalmutation: 'Germline Causal Mutation',
  germlinemodifyingmutation: 'Germline Modifying Mutation',
  somaticcausalmutation: 'Somatic Causal Mutation',
};

export function toBackendAssociationTypeValue(value: string): string {
  return ASSOCIATION_TYPE_BACKEND_VALUES[normalizeAssociationTypeToken(value)] ?? value;
}

function mapAssociationTypePart(value: unknown): string[] {
  const str = String(value);
  if (isDrugResponseAssociationType(str)) {
    return [...DRUG_RESPONSE_EXPANDED_VALUES];
  }
  return [toBackendAssociationTypeValue(str)];
}

export function expandAssociationTypeValue(filterValue: unknown): unknown {
  if (Array.isArray(filterValue)) {
    const result: string[] = [];
    for (const v of filterValue) {
      for (const mapped of mapAssociationTypePart(v)) {
        if (!result.includes(mapped)) result.push(mapped);
      }
    }
    return result;
  }

  if (typeof filterValue === 'string') {
    const parts = filterValue.split(',').map(s => s.trim()).filter(Boolean);
    const expandedParts: string[] = [];
    for (const part of parts) {
      for (const mapped of mapAssociationTypePart(part)) {
        if (!expandedParts.includes(mapped)) expandedParts.push(mapped);
      }
    }
    return expandedParts.join(',');
  }

  return filterValue;
}

export function buildAssociationTypeFilterOptions(...groups: readonly (readonly string[])[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const group of groups) {
    for (const type of group) {
      const key = normalizeAssociationTypeToken(formatAssociationType(type));
      if (!seen.has(key)) {
        seen.add(key);
        result.push(type);
      }
    }
  }

  return result.sort((a, b) => formatAssociationType(a).localeCompare(formatAssociationType(b)));
}

/** Full GDA association-type filter list. */
export const ASSOCIATION_TYPES_GDA_FILTER = buildAssociationTypeFilterOptions(
  RELATION_EXTRACTION_ASSOCIATION_TYPES,
  EXTENDED_ASSOCIATION_TYPES_GDA,
  LEGACY_ASSOCIATION_TYPES_GDA,
);

/** Full VDA association-type filter list. */
export const ASSOCIATION_TYPES_VDA_FILTER = buildAssociationTypeFilterOptions(
  RELATION_EXTRACTION_ASSOCIATION_TYPES,
  EXTENDED_ASSOCIATION_TYPES_VDA,
  LEGACY_ASSOCIATION_TYPES_VDA,
);

export function dropdownFilterMatchesSearch(
  option: { id?: string; label?: string },
  searchTerm: string,
): boolean {
  if (!searchTerm) return true;

  const normalizedTerm = normalizeAssociationTypeToken(searchTerm);
  const label = option.label ?? '';
  const id = option.id ?? '';
  const lowerTerm = searchTerm.toLowerCase();

  const matchesDirectly =
    normalizeAssociationTypeToken(label).includes(normalizedTerm) ||
    normalizeAssociationTypeToken(id).includes(normalizedTerm) ||
    label.toLowerCase().includes(lowerTerm) ||
    id.toLowerCase().includes(lowerTerm);

  if (matchesDirectly) return true;

  if (
    normalizedTerm.includes('drugresponse') &&
    (normalizeAssociationTypeToken(label) === 'drugresponse' ||
      normalizeAssociationTypeToken(id) === 'drugresponse')
  ) {
    return true;
  }

  return false;
}
